/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Charts Overview", description = "Learn what how to configure, save, and update charts", lastUpdatedDate = new Date(1790208000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Charts Overview",
        description: "Learn what how to configure, save, and update charts",
        lastUpdatedDate: "Thu Sep 24 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Chart types",
          "depth": 0,
          "id": "chart-types"
        }, {
          "value": "Key elements",
          "depth": 0,
          "id": "key-elements"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ul: "ul",
      li: "li",
      a: "a",
      img: "img",
      strong: "strong",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "chart-types",
        children: "Chart types"
      }), "\n", _jsx(_components.p, {
        children: "Charts are the building blocks of Hightouch's Intelligence features. We currently support several chart types:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://hightouch.com/docs/campaign-intelligence/insights",
            children: "Insights"
          }), ": Analyze audience composition, breakdowns, and performance."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://hightouch.com/docs/campaign-intelligence/funnels",
            children: "Funnels"
          }), ": Identify drop-off points across a sequence of events."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://hightouch.com/docs/campaign-intelligence/splits",
            children: "Splits"
          }), ": Measure results of experiments between randomized groups."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "key-elements",
        children: "Key elements"
      }), "\n", _jsx(_components.p, {
        children: "While the analysis varies between chart types, the general anatomy stays consistent across them."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/chart_definitions.png",
          alt: "Chart Anatomy"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Parent model:"
          }), " The dataset for your analysis, defined in the schema setup step."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Chart type:"
          }), " The analysis type (Insights, Funnels, Splits)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Metrics:"
          }), " An aggregation over events. Metrics can have an aggregation type, filters, and an attribution window. Metrics can be on the cohort level (sum of purchases for all users) or on the user level (average purchase amount per user)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Segment by:"
          }), " The segment of users to include in the analysis. Default is to include the entire parent model. You can select multiple existing audiences or create inline audiences. If using an attribution window, events are only included if the user falls within the attribution window for the audience-metric pair."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Group by:"
          }), " The dimension to breakdown a metric by. There are several types of dimensions:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "User properties: Attributes, traits, or merge columns of a user. Examples: gender, state, loyalty status"
            }), "\n", _jsx(_components.li, {
              children: "Event properties: Attributes on the specific event. Examples: brand, campaign, utms."
            }), "\n", _jsx(_components.li, {
              children: "Related properties: Attributes on a related model. Examples: item, item property."
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Time interval:"
          }), " Time granularity for metric rollup (daily, weekly, monthly). For example, sessions from distinct users per day = DAU, per week = WAU."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Time window:"
          }), " The time period for analysis. Events outside this window won't contribute to metrics."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Saved charts:"
          }), " Drawer showing all saved charts across the workspace."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Export as CSV:"
          }), " Option to download the chart's datapoints as CSV."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sampling:"
          }), " For parent models that have Sampling turned on, enable ", _jsx(_components.code, {
            children: "Fast queries"
          }), " to query a sampled percentage of your parent model."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Mode:"
          }), " The type of data aggregation (", _jsx(_components.code, {
            children: "Over time"
          }), " or ", _jsx(_components.code, {
            children: "Cumulative"
          }), ") when viewing time-series charts (Line, Column, and Area)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Visualization:"
          }), " The type of data visualization (Line, Column, Area, Bar, Table, Heat map, Number)."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
